import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { Button } from './Button';
import { useNavigate, useLocation } from 'react-router-dom';
// Removed unused framer-motion import

const NavContainer = styled.nav<{ isScrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: ${({ isScrolled }) =>
    isScrolled
      ? 'rgba(40, 82, 111, 0.95)'
      : 'linear-gradient(135deg, rgba(40,82,111,0.85), rgba(64,120,170,0.75))'};
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 30px rgba(40, 82, 111, 0.15);
  z-index: 1000;
  transition: all 0.3s ease;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const NavContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  min-height: 80px; /* Standard navbar height */
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    min-height: 70px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoWrapper = styled.div`
  height: 60px; /* Keep navbar standard height */
  width: 400px; /* More space for larger logo */
  position: relative;
  overflow: visible;
  display: flex;
  align-items: center;
  padding-left: 28px; /* left breathing room */

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 50px;
    width: 350px;
    padding-left: 20px;
  }
`;

// Removed LogoVariant type - using single consistent logo color

const LogoMark = styled.div`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  height: 336px; /* 7x bigger: 48px * 7 */
  width: 1260px; /* 7x bigger: 180px * 7 */
  -webkit-mask: url('/img_to_use/De-arc.svg') no-repeat left center;
  mask: url('/img_to_use/De-arc.svg') no-repeat left center;
  -webkit-mask-size: contain;
  mask-size: contain;
  background-color: #FFFFFF; /* Clean white color */
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.15)) drop-shadow(0 1px 3px rgba(0,0,0,0.1));
  user-select: none;
  -webkit-user-drag: none;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    filter: drop-shadow(0 3px 12px rgba(255,255,255,0.4)) drop-shadow(0 1px 3px rgba(0,0,0,0.2));
    transform: translateY(-50%) scale(1.02);
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    height: 280px; /* 7x bigger mobile: 40px * 7 */
    width: 1050px; /* 7x bigger mobile: 150px * 7 */
  }
`;

// Removed text fallback logo per user request; SVG-only logo is used

const NavLinks = styled.div<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.tablet}) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    transform: ${({ isOpen }) => isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease;
    z-index: 999;
  }
`;

const NavLink = styled.a<{ isActive: boolean; isScrolled: boolean }>`
  color: ${({ isActive, isScrolled }) => 
    isActive 
      ? (isScrolled ? theme.colors.accentSoft : theme.colors.accent)
      : (isScrolled ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.95)')
  };
  text-decoration: none;
  font-weight: ${({ isActive }) => isActive ? 600 : 500};
  font-size: 1rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    color: ${({ isScrolled }) => isScrolled ? 'white' : theme.colors.accent};
    background-color: ${({ isScrolled }) => 
      isScrolled ? 'rgba(255, 255, 255, 0.10)' : 'rgba(255, 255, 255, 0.12)'};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    width: ${({ isActive }) => isActive ? '80%' : '0'};
    height: 2px;
    background-color: ${({ isScrolled }) => isScrolled ? 'rgba(255, 255, 255, 0.9)' : theme.colors.accent};
    transform: translateX(-50%);
    transition: width 0.3s ease;
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 1.2rem;
    padding: 1rem 2rem;
    color: ${({ isActive }) => isActive ? theme.colors.primary : theme.colors.text};
    
    &:hover {
      color: ${theme.colors.primary};
      background-color: rgba(0, 51, 102, 0.05);
    }
  }
`;

const NavButton = styled(Button)<{ isScrolled: boolean }>`
  background: ${({ isScrolled }) => (isScrolled ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.95)')};
  color: ${theme.colors.primary};
  box-shadow: 0 2px 10px rgba(0,0,0,0.12);
  border-radius: 14px;
  padding: 0.6rem 1.2rem;
  font-weight: 700;
  &:hover {
    background: white;
  }
  @media (max-width: ${theme.breakpoints.tablet}) {
    margin-top: 1rem;
  }
`;

const MobileMenuButton = styled.button<{ isScrolled: boolean }>`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ isScrolled }) => isScrolled ? 'white' : 'white'};
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;
  transition: all 0.3s ease;

  @media (max-width: ${theme.breakpoints.tablet}) {
    display: block;
  }

  &:hover {
    opacity: 0.7;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: none;
  border: none;
  font-size: 2rem;
  color: ${theme.colors.text};
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1002;

  &:hover {
    opacity: 0.7;
  }

  @media (min-width: calc(${theme.breakpoints.tablet} + 1px)) {
    display: none;
  }
`;

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  // Removed unused logo loading state
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <NavContainer isScrolled={isScrolled}>
      <NavContent>
        <LogoContainer>
          <LogoWrapper>
            <LogoMark
              onClick={(e) => {
                e.preventDefault();
                handleNavigation('/');
              }}
            />
          </LogoWrapper>
        </LogoContainer>

        <NavLinks isOpen={isOpen}>
          <CloseButton onClick={() => setIsOpen(false)}>
            ×
          </CloseButton>
          
          <NavLink
            href="/"
            isActive={isActive('/')}
            isScrolled={isScrolled}
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/');
            }}
          >
            Home
          </NavLink>
          
          <NavLink
            href="/about"
            isActive={isActive('/about')}
            isScrolled={isScrolled}
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/about');
            }}
          >
            About
          </NavLink>
          
          <NavLink
            href="/services"
            isActive={isActive('/services')}
            isScrolled={isScrolled}
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/services');
            }}
          >
            Services
          </NavLink>
          
          <NavLink
            href="/process"
            isActive={isActive('/process')}
            isScrolled={isScrolled}
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/process');
            }}
          >
            Process
          </NavLink>
          
          <NavLink
            href="/contact"
            isActive={isActive('/contact')}
            isScrolled={isScrolled}
            onClick={(e) => {
              e.preventDefault();
              handleNavigation('/contact');
            }}
          >
            Contact
          </NavLink>

          <NavButton
            variant="primary"
            size="small"
            isScrolled={isScrolled}
            onClick={() => handleNavigation('/contact')}
          >
            Get Started
          </NavButton>
        </NavLinks>

        <MobileMenuButton
          isScrolled={isScrolled}
          onClick={() => setIsOpen(!isOpen)}
        >
          ☰
        </MobileMenuButton>
      </NavContent>
    </NavContainer>
  );
};

export default Navbar; 